/**
 * `/macro/positioning` — funding stress and the crowded long / short spread,
 * the nightly derive's numbers for the Positioning & liquidity section
 * (`services/qv-data/app/routers/macro_derived.py` + `app/macro_derive/read.py`,
 * branch `macro/derive`; `docs/plans/macro.md`).
 *
 * **Admin only** on the server too (`admin_only`), and a 503 until the `macro`
 * artifact has been pulled to the serving machine: both come back as `null`
 * here, and the section says the numbers are not built yet.
 *
 * The payload is **columnar** — one list per column — and stays columnar here:
 * a two-year daily series is ten arrays, not five hundred objects. Every column
 * is mapped by name, as `api/holdings.ts`: a column the server renames fails in
 * a type here rather than going quietly missing.
 *
 * The chart tier serves it, the same host as `/holdings` and `/congress`.
 */
import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = (httpBase(import.meta.env.VITE_QV_SYM_URL, import.meta.env.VITE_QV_SET_URL) || "").replace(/\/$/, "");

type Num = number | null;

/** The de-grossing proxy, daily. Every leg is % against SPY, close to close. */
export interface DegrossColumns {
  date: string[];
  /** Crowded longs: our 13F basket. */
  lg: Num[];
  /** Crowded longs: GVIP. */
  gv: Num[];
  /** Most shorted (days to cover). */
  sh: Num[];
  /** Momentum long / short, top minus bottom decile. */
  mo: Num[];
  /** Momentum's z against the 60 days strictly before; published so the σ line can be re-struck here. */
  moZ: Num[];
  /** Spread index, 100 at the start: our 13F basket − most shorted. */
  idx: Num[];
  /** Spread index: GVIP − most shorted. */
  gIdx: Num[];
  /** Percent below the running peak of `idx` / `gIdx`. */
  dd: Num[];
  gDd: Num[];
  /** Unwind days struck at the server's σ — the section re-strikes them at its own. */
  unwind: boolean[];
  gUnwind: boolean[];
  nLong: Num[];
  nShort: Num[];
}

/** Funding conditions, daily (Massive `/fed/v1/funding-conditions`). */
export interface FundingColumns {
  date: string[];
  /** SOFR − IORB, basis points. */
  spreadBp: Num[];
  /** Its trailing average over `fundingAvgDays` observations. */
  avgBp: Num[];
  tight: (boolean | null)[];
  /** Overnight reverse repo, billions of dollars. */
  rrp: Num[];
  sofr: Num[];
  iorb: Num[];
}

/** The owner's parameters the numbers were struck at (`app/macro_derive/params.py`). */
export interface PositioningParams {
  unwindSigma: number;
  unwindLookbackDays: number;
  unwindSpreadPct: number;
  unwindRecentDays: number;
  degrossDrawdownPct: number;
  fundingAvgDays: number;
  fundingTightBp: number;
  basketSize: number;
  priceMin: number;
  advMin: number;
}

export interface Positioning {
  builtAt: string | null;
  /** Inputs the derive could not read, e.g. a short-interest table not built yet. */
  absent: string[];
  params: PositioningParams;
  degross: DegrossColumns;
  funding: FundingColumns;
}

/** The mock's and the plan's defaults, for a parameter the payload leaves out. */
export const DEFAULT_PARAMS: PositioningParams = {
  unwindSigma: 2,
  unwindLookbackDays: 60,
  unwindSpreadPct: 1,
  unwindRecentDays: 20,
  degrossDrawdownPct: -5,
  fundingAvgDays: 20,
  fundingTightBp: 0,
  basketSize: 50,
  priceMin: 5,
  advMin: 20e6,
};

const opt = (v: unknown): Num => (typeof v === "number" && Number.isFinite(v) ? v : null);
const nums = (v: unknown): Num[] => (Array.isArray(v) ? v.map(opt) : []);
const bools = (v: unknown): boolean[] => (Array.isArray(v) ? v.map((x) => x === true) : []);
const optBools = (v: unknown): (boolean | null)[] =>
  Array.isArray(v) ? v.map((x) => (typeof x === "boolean" ? x : null)) : [];
const dates = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => String(x ?? "").slice(0, 10)) : []);

export function mapPositioning(j: any): Positioning {
  const p = j?.params ?? {};
  const param = (k: keyof PositioningParams) => opt(p[k]) ?? DEFAULT_PARAMS[k];
  const d = j?.degross ?? {};
  const f = j?.funding ?? {};
  return {
    builtAt: typeof j?.builtAt === "string" ? j.builtAt : null,
    absent: Array.isArray(j?.absent) ? j.absent.map(String) : [],
    params: {
      unwindSigma: param("unwindSigma"),
      unwindLookbackDays: param("unwindLookbackDays"),
      unwindSpreadPct: param("unwindSpreadPct"),
      unwindRecentDays: param("unwindRecentDays"),
      degrossDrawdownPct: param("degrossDrawdownPct"),
      fundingAvgDays: param("fundingAvgDays"),
      fundingTightBp: param("fundingTightBp"),
      basketSize: param("basketSize"),
      priceMin: param("priceMin"),
      advMin: param("advMin"),
    },
    degross: {
      date: dates(d.date),
      lg: nums(d.lg),
      gv: nums(d.gv),
      sh: nums(d.sh),
      mo: nums(d.mo),
      moZ: nums(d.moZ),
      idx: nums(d.idx),
      gIdx: nums(d.gIdx),
      dd: nums(d.dd),
      gDd: nums(d.gDd),
      unwind: bools(d.unwind),
      gUnwind: bools(d.gUnwind),
      nLong: nums(d.nLong),
      nShort: nums(d.nShort),
    },
    funding: {
      date: dates(f.date),
      spreadBp: nums(f.spreadBp),
      avgBp: nums(f.avgBp),
      tight: optBools(f.tight),
      rrp: nums(f.rrp),
      sofr: nums(f.sofr),
      iorb: nums(f.iorb),
    },
  };
}

/** null when the numbers are not built (503), the caller is not an admin (403), or the server cannot be reached. */
export async function loadPositioning(): Promise<Positioning | null> {
  if (!BASE) return null;
  try {
    const res = await fetch(`${BASE}/macro/positioning`, { headers: await requestHeaders() });
    if (!res.ok) return null;
    return mapPositioning(await res.json());
  } catch {
    return null;
  }
}
